import { call, put, takeEvery, select } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import i18n from '../../i18n';

import { RootState } from '../reducers/rootReducer';
import { getExternalConfigValues, setRefreshOrdersInterval } from '../actions/config.actions';
import { setCurrentCountry } from '../actions/currentCountry.actions';
import { setCurrentLanguage } from '../actions/currentLanguage.actions';
import { setLoading } from '../actions/loading.actions';
import { mapGetExternalConfigResponseToSetRefreshOrdersIntervalPayload } from './getExternalConfig.mappers';

import {
  SAGA_GET_EXTERNAL_CONFIG,
  BFF_EXTERNAL_CONFIG,
  BFF_V1,
  PICKING_APIGEE_BASE_PATH,
  COUNTRY_MEXICO,
  COUNTRY_BRAZIL,
  LANGUAGE_ENGLISH,
  LANGUAGE_SPANISH,
  LANGUAGE_PORTUGUESE,
  PREFERRED_LANGUAGE,
} from '../../constants';

import { ConfigAuthValues, Language } from '../../types';

import { callAPI, createApiResponseTimeHelper } from '../../utils';

import { handleApiErrors, catchApiErrors } from './utils';
import { getOrders } from '../actions/orders.actions';

const callGetExternalConfig = (storeID: string, configAuthValues: ConfigAuthValues) => {
  return callAPI({
    method: 'GET',
    url: `${configAuthValues.APIGEE_BASE_URL}${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_EXTERNAL_CONFIG}/${storeID}`,
  });
};

const getStoredPreferredLanguage = (): Language | null => {
  let preferredLanguage: string | null = null;

  try {
    preferredLanguage = sessionStorage.getItem(PREFERRED_LANGUAGE);
  } catch (_e) {
    // storage unavailable; fall back to default language
    return null;
  }

  if (
    preferredLanguage === LANGUAGE_ENGLISH ||
    preferredLanguage === LANGUAGE_SPANISH ||
    preferredLanguage === LANGUAGE_PORTUGUESE
  ) {
    return preferredLanguage;
  }

  return null;
};

export function* runGetExternalConfigSaga(action: ReturnType<typeof getExternalConfigValues>): Generator<any, void, any> {
  yield put(setLoading({ sagaName: SAGA_GET_EXTERNAL_CONFIG, isLoading: true }));
  const configAuthValues: ConfigAuthValues = yield select((state: RootState) => state.config.authValues);

  const getApiResponseTime = createApiResponseTimeHelper();

  try {
    const result: any = yield call(callGetExternalConfig, action.payload.storeID, configAuthValues);
    
    const apiResponseTime = getApiResponseTime();

    let defaultLanguage: Language = LANGUAGE_ENGLISH;
    if (result.data.config.countryCd) {
      const {countryCd} = result.data.config;
      yield put(setCurrentCountry(countryCd));
      if (countryCd === COUNTRY_MEXICO) defaultLanguage = LANGUAGE_SPANISH;
      else if (countryCd === COUNTRY_BRAZIL) defaultLanguage = LANGUAGE_PORTUGUESE;
    }

    const language = getStoredPreferredLanguage() || defaultLanguage;
    yield put(setCurrentLanguage(language));
    i18n.changeLanguage(language);
    
    // TODO: Validate API response
    yield put(setRefreshOrdersInterval(mapGetExternalConfigResponseToSetRefreshOrdersIntervalPayload(result.data)));
    
    yield handleApiErrors(result.data.errors, {
      saga: SAGA_GET_EXTERNAL_CONFIG,
      method: 'GET',
      url: `${configAuthValues.APIGEE_BASE_URL}${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_EXTERNAL_CONFIG}/${action.payload.storeID}`,
      data: {},
      config: configAuthValues
    }, apiResponseTime);
    
    yield put(getOrders({ fulfillmentStoreID: action.payload.storeID }));
  } catch (error) {
    const apiResponseTime = getApiResponseTime();

    try {
      const {storeID} = action.payload;
      const url = `${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_EXTERNAL_CONFIG}/${storeID}`;
      const method = 'GET';
      const data = {};
      const config = configAuthValues;
      
      yield catchApiErrors(error as any, {url, method, data, config, saga: SAGA_GET_EXTERNAL_CONFIG}, apiResponseTime);
    } catch (_error) {
      yield catchApiErrors(error as any, null, apiResponseTime);
    }
  }

  yield put(setLoading({ sagaName: SAGA_GET_EXTERNAL_CONFIG, isLoading: false }));
}

export default function* watchGetOrders() {
  yield takeEvery(getType(getExternalConfigValues), runGetExternalConfigSaga);
}
